import { Card } from "flowbite-react";
import React, { useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import {
  countDadosCadastrais,
  formatDocument,
  formatToReal,
} from "../../../utils/utils";
import Sociedade from "../../common/query/Sociedade";
import QuadroSocietario from "../../common/query/QuadroSocietario";

const CadastralPremiumPJQuery = ({ data, dataFull }) => {
  const dc = data?.dadosCadastrais || {};
  const [copied, setCopied] = useState({ cnpj: false, cep: false });

  const handleCopy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied((p) => ({ ...p, [key]: true }));
      setTimeout(() => setCopied((p) => ({ ...p, [key]: false })), 1200);
    } catch (_) {}
  };

  // capital social seguro (aceita "R$ 30.000,00" ou número)
  const capitalMostrar =
    typeof dc?.capitalInicial === "number"
      ? formatToReal(dc.capitalInicial)
      : (dc?.capitalInicial || "").toString().trim().startsWith("R$")
      ? dc.capitalInicial
      : dc?.capitalInicial
      ? formatToReal(
          Number(
            String(dc.capitalInicial)
              .replace(/[^\d,.-]/g, "")
              .replace(/\./g, "")
              .replace(",", ".")
          )
        )
      : "Indisponível";

  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  // Só renderiza se houver exatamente 1
  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 2;

  return (
    <>
      {podeRenderizarDadosCadastrais && (
        <>
          {" "}
          <TitleStripe>🏢 Dados Cadastrais</TitleStripe>
          <div className="border border-gray-200 rounded-xl p-6 bg-white">
            {/* Grid principal */}
            <div className="grid grid-cols-2 gap-6 text-base">
              <div>
                <p className="text-gray-500 font-medium">Razão Social</p>
                <p className="text-gray-800">
                  {dc?.razaoSocial || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">CNPJ</p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-800">
                    {formatDocument(dc?.cnpj) || "Indisponível"}
                  </p>
                  {!!dc?.cnpj && (
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(formatDocument(dc?.cnpj), "cnpj")
                      }
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 px-2 py-1"
                      title="Copiar CNPJ"
                    >
                      {copied.cnpj ? "✔️" : "📋"}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Nome Fantasia</p>
                <p className="text-gray-800">
                  {dc?.nomeFantasia || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Data de Fundação</p>
                <p className="text-gray-800">
                  {dc?.dataFundacao || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Natureza Jurídica</p>
                <p className="text-gray-800">
                  {dc?.naturezaJuridica || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">
                  CNAE / Ramo de Atividade
                </p>
                <p className="text-gray-800">
                  {dc?.ramoAtividade || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Capital Social</p>
                <p className="text-gray-800">{capitalMostrar}</p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Inscrição Estadual</p>
                <p className="text-gray-800">
                  {dc?.inscricaoEstadual || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Endereço</p>
                <p className="text-gray-800">
                  {[dc?.endereco, dc?.numero, dc?.complemento]
                    .filter(Boolean)
                    .join(", ") || "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Bairro</p>
                <p className="text-gray-800">{dc?.bairro || "Indisponível"}</p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Cidade / UF</p>
                <p className="text-gray-800">
                  {[dc?.cidade, dc?.uf].filter(Boolean).join("/") ||
                    "Indisponível"}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">CEP</p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-800">{dc?.cep || "Indisponível"}</p>
                  {!!dc?.cep && (
                    <button
                      type="button"
                      onClick={() => handleCopy(dc?.cep, "cep")}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 px-2 py-1"
                      title="Copiar CEP"
                    >
                      {copied.cep ? "✔️" : "📋"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Chips de resumo opcionais */}
            <div className="flex flex-wrap gap-2 mt-6">
              {dc?.cidade && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-700">
                  📍 {dc.cidade}
                  {dc?.uf ? `/${dc.uf}` : ""}
                </span>
              )}
              {dc?.ramoAtividade && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-700">
                  🏷️ {dc.ramoAtividade}
                </span>
              )}
              {(dc?.situacao || "").toUpperCase() === "ATIVO" && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-green-200 bg-green-50 text-green-700">
                  ✅ Ativa
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {data?.socios && (
        <div>
          <TitleStripe>Sócios</TitleStripe>
          {data?.socios?.length > 0 ? (
            <Sociedade socios={data?.socios} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>📍 Endereços</TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>📧 E-mails</TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>☎️ Telefones</TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </>
  );
};

export default CadastralPremiumPJQuery;
